import { useState } from "react";
import { Scale, Search, GraduationCap, Code, Palette, Globe2, Languages, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";

const CATEGORIES = [
  { icon: Scale, t: "Lawyers" }, { icon: Search, t: "Researchers" }, { icon: GraduationCap, t: "Students" },
  { icon: Code, t: "Cybersecurity Experts" }, { icon: Palette, t: "Designers" }, { icon: Code, t: "Developers" },
  { icon: Languages, t: "Translators" }, { icon: GraduationCap, t: "Educators" },
];

const BENEFITS = [
  "Real research contribution", "Global collaboration", "Mentorship from senior fellows", "Publication credits",
];

export default function Volunteer() {
  const [form, setForm] = useState({ full_name: "", email: "", country: "", category: "Researchers", skills: "", availability: "", motivation: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/volunteer", form); toast.success("Application received. Thank you."); setForm({ ...form, motivation: "", skills: "" }); }
    catch { toast.error("Submission failed."); }
    finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="volunteer-page">
      <SectionTitle eyebrow="VOLUNTEER" title="Lend your skills to a global mission." subtitle="Researchers, lawyers, developers, translators, designers, educators — every discipline matters." />

      <div className="mt-10 grid md:grid-cols-4 gap-3">
        {CATEGORIES.map((c) => (
          <button key={c.t} onClick={() => setForm({ ...form, category: c.t })} data-testid={`vol-cat-${c.t}`} className={`rounded-2xl border p-5 text-left transition-colors ${form.category === c.t ? "bg-gold/10 border-gold/50" : "bg-obsidian-soft border-white/5 hover:border-gold/30"}`}>
            <c.icon className="h-5 w-5 text-gold" />
            <div className="font-display mt-3">{c.t}</div>
          </button>
        ))}
      </div>

      <div className="mt-12 grid lg:grid-cols-[1.4fr,1fr] gap-6">
        <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
          <CardContent className="p-8">
            <div className="font-display text-2xl">Application</div>
            <form onSubmit={submit} className="mt-6 grid md:grid-cols-2 gap-4" data-testid="volunteer-form">
              <Input required placeholder="Full name" value={form.full_name} onChange={upd("full_name")} data-testid="vol-full-name" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Input required type="email" placeholder="Email" value={form.email} onChange={upd("email")} data-testid="vol-email" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Input required placeholder="Country" value={form.country} onChange={upd("country")} data-testid="vol-country" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Input required placeholder="Availability (e.g., 5 hrs/week)" value={form.availability} onChange={upd("availability")} data-testid="vol-availability" className="bg-black border-white/10 rounded-xl h-11 text-white" />
              <Textarea required placeholder="Skills" value={form.skills} onChange={upd("skills")} data-testid="vol-skills" className="md:col-span-2 bg-black border-white/10 rounded-xl text-white" />
              <Textarea required placeholder="Why do you want to volunteer with IOCRI?" value={form.motivation} onChange={upd("motivation")} data-testid="vol-motivation" className="md:col-span-2 bg-black border-white/10 rounded-xl text-white" />
              <Button type="submit" disabled={loading} data-testid="vol-submit" className="md:col-span-2 rounded-full bg-gold text-black hover:bg-gold-light h-11">
                {loading ? "Submitting…" : "Submit Application"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-obsidian-soft border-white/5 rounded-2xl">
          <CardContent className="p-8">
            <Sparkles className="h-6 w-6 text-gold" />
            <div className="font-display text-2xl mt-4">Benefits</div>
            <ul className="mt-4 space-y-2 text-white/80">
              {BENEFITS.map((b) => (<li key={b} className="flex gap-2"><Globe2 className="h-4 w-4 text-gold mt-1" />{b}</li>))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
